/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */

// Viewport service - React-based, extractable
import { ServiceBase } from '../types/service-event-bridge.type';

// deep import, not the @r-core barrel: that barrel pulls in the controller, which imports this file
import { getRenderedContentBounds } from '../../core/helpers/get-rendered-content-bounds';
import { getViewerSize } from './functions/get-viewer-size';
import { viewportDefaults } from './viewport-defaults';
import { computeEquivalentRotationFromHistory, EquivalentRotationResult } from './viewport-service.compute-rotation.cfn';
import { ViewerSizeType, ViewportActions, ViewportService, ViewportState } from './types';
import { ViewportConfig, ViewportConfigIn, ViewportConfigOut } from './types/viewport-config.type';

type ContainerSizeType = { width: number; height: number; ratio: number };

export class ViewportServiceClass extends ServiceBase implements ViewportService {
	// ============================================================================
	// PUBLIC VARIABLES
	// ============================================================================
	// No public variables in this class

	// ============================================================================
	// PRIVATE VARIABLES
	// ============================================================================
	#listeners: Set<(state: ViewportState) => void> = new Set();
	#containerSizeListeners: Set<(containerSize: { width: number; height: number; ratio: number }) => void> = new Set();
	#viewerSizeListeners: Set<(viewerSize: ViewerSizeType | null) => void> = new Set();
	// #config represents the mutable configuration of the viewport
	#config: ViewportConfig;
	// #state represents the immutable state of the viewport
	#state: ViewportState;
	#isZooming = false;
	#zoomAnimationId: number | null = null;
	#isRotating = false;
	#rotationAnimationId: number | null = null;
	#isPanning = false;
	#panAnimationId: number | null = null;

	// Animation queues
	#zoomQueue: Array<{ target: number; duration?: number }> = [];
	#panQueue: Array<{ targetX: number; targetY: number; duration?: number }> = [];
	#rotationQueue: Array<{ target: number; duration?: number }> = [];
	#containerSize: ContainerSizeType = { width: 0, height: 0, ratio: 0 };
	#viewerSize: ViewerSizeType | null = null;
	#renderCallback?: () => void;
	#svgElement: SVGElement | null = null; // Reference to the registered SVG element

	// ============================================================================
	// READONLY VARIABLES
	// ============================================================================
	// No readonly variables in this class

	// ============================================================================
	// CONSTRUCTOR
	// ============================================================================
	constructor (config: ViewportConfigIn = {}, renderCallback?: () => void) {
		super();
		this.#state = {
			zoom: config.zoom ?? viewportDefaults.zoom,
			rotation: config.rotation ?? viewportDefaults.rotation,
			xPan: config.xPan ?? viewportDefaults.xPan,
			yPan: config.yPan ?? viewportDefaults.yPan,
			targetZoom: config.zoom ?? viewportDefaults.zoom,
			targetRotation: config.rotation ?? viewportDefaults.rotation,
			targetXPan: config.xPan ?? viewportDefaults.xPan,
			targetYPan: config.yPan ?? viewportDefaults.yPan,
			rotationHistory: [
				{
					rotation: config.rotation ?? viewportDefaults.rotation,
					panX: config.xPan ?? viewportDefaults.xPan,
					panY: config.yPan ?? viewportDefaults.yPan,
					timestamp: Date.now()
				}
			]
		};

		// Initialize config with defaults
		this.#config = {
			...viewportDefaults
		} as ViewportConfig;

		// Apply config overrides only if they are valid
		if (typeof config.minZoom === 'number') {
			this.#config.minZoom = config.minZoom;
		}
		if (typeof config.maxZoom === 'number') {
			this.#config.maxZoom = config.maxZoom;
		}
		if (typeof config.zoomSnap === 'number') {
			this.#config.zoomSnap = config.zoomSnap;
		}
		if (typeof config.smoothZoomEnabled === 'boolean') {
			this.#config.smoothZoomEnabled = config.smoothZoomEnabled;
		}
		if (typeof config.smoothZoomDuration === 'number') {
			this.#config.smoothZoomDuration = config.smoothZoomDuration;
		}
		if (typeof config.smoothZoomEasing === 'string') {
			this.#config.smoothZoomEasing = config.smoothZoomEasing;
		}
		if (typeof config.smoothRotationEnabled === 'boolean') {
			this.#config.smoothRotationEnabled = config.smoothRotationEnabled;
		}
		if (typeof config.smoothRotationDuration === 'number') {
			this.#config.smoothRotationDuration = config.smoothRotationDuration;
		}
		if (typeof config.smoothRotationEasing === 'string') {
			this.#config.smoothRotationEasing = config.smoothRotationEasing;
		}
		if (typeof config.smoothPanEnabled === 'boolean') {
			this.#config.smoothPanEnabled = config.smoothPanEnabled;
		}
		if (typeof config.smoothPanDuration === 'number') {
			this.#config.smoothPanDuration = config.smoothPanDuration;
		}
		if (typeof config.smoothPanEasing === 'string') {
			this.#config.smoothPanEasing = config.smoothPanEasing;
		}
		if (typeof config.maxFPS === 'number' && config.maxFPS > 0) {
			this.#config.maxFPS = config.maxFPS;
		}
		if (typeof config.enablePan === 'boolean') {
			this.#config.enablePan = config.enablePan;
		}
		if (typeof config.panSensitivity === 'number') {
			this.#config.panSensitivity = config.panSensitivity;
		}
		if (typeof config.middleClickPanEnabled === 'boolean') {
			this.#config.middleClickPanEnabled = config.middleClickPanEnabled;
		}
		if (typeof config.middleClickPanSensitivity === 'number') {
			this.#config.middleClickPanSensitivity = config.middleClickPanSensitivity;
		}
		if (typeof config.fitToViewPadding === 'number') {
			this.#config.fitToViewPadding = config.fitToViewPadding;
		}

		this.#renderCallback = renderCallback;

		// Don't initialize with default size - wait for actual container dimensions
		// This keeps the service in initializing state until properly sized
	}

	// ============================================================================
	// PUBLIC ACTIONS (Main operational API)
	// ============================================================================
	get actions (): ViewportActions {
		return {
			setZoom: this._setZoom.bind(this),
			setPan: this._setPan.bind(this),
			resetView: this._resetView.bind(this),
			resetSizeState: this._resetSizeState.bind(this),
			fitToView: this._fitToView.bind(this),
			smoothZoom: this._queueZoomAnimation.bind(this),
			cancelZoomAnimation: this._cancelZoomAnimation.bind(this),
			setRotation: this._setRotation.bind(this),
			smoothRotation: this._queueRotationAnimation.bind(this),
			cancelRotationAnimation: this._cancelRotationAnimation.bind(this),
			smoothPan: this._queuePanAnimation.bind(this),
			cancelPanAnimation: this._cancelPanAnimation.bind(this),
			updateSize: this._updateSize.bind(this),
			transformCoordinates: this._transformCoordinates.bind(this),
			getSvgTransform: this._getSvgTransform.bind(this),
			getSvgElement: this.getSvgElement.bind(this),
			registerSvg: this._registerSvg.bind(this),
			unregisterSvg: this._unregisterSvg.bind(this),
			getEquivalentRotation: this.getEquivalentRotation.bind(this),
			triggerAutoZoom: this.triggerAutoZoom.bind(this)
		};
	}

	/**
	 * Compute a single equivalent rotation angle and center for the current rotation history
	 */
	public getEquivalentRotation (): EquivalentRotationResult {
		return computeEquivalentRotationFromHistory(this.#state.rotationHistory);
	}

	/**
	 * Get the currently registered SVG element
	 * Returns null if no SVG element is registered
	 */
	public getSvgElement (): SVGElement | null {
		return this.#svgElement;
	}

	// ============================================================================
	// GETTERS/SETTERS
	// ============================================================================
	get config (): ViewportConfigOut {
		return {
			...this.#config,
			zoom: this.#state.zoom,
			rotation: this.#state.rotation,
			xPan: this.#state.xPan,
			yPan: this.#state.yPan
		};
	}

	get state (): ViewportState {
		return { ...this.#state };
	}

	get isZooming (): boolean {
		// Ensure isZooming is initialized
		if (typeof this.#isZooming !== 'boolean') {
			this.#isZooming = false;
		}
		return this.#isZooming;
	}

	get isRotating (): boolean {
		return this.#isRotating;
	}

	get isPanning (): boolean {
		return this.#isPanning;
	}

	get containerSize (): ContainerSizeType {
		// Ensure container size is initialized
		if (!this.#containerSize || !this.#containerSize.width || !this.#containerSize.height) {
			this.#containerSize = {
				width: 0,
				height: 0,
				ratio: 0
			};
		}
		return { ...this.#containerSize };
	}

	get viewerSize (): ViewerSizeType | null {
		// Return null when in initializing state (no container size set)
		if (!this.#containerSize || this.#containerSize.width <= 0 || this.#containerSize.height <= 0) {
			return null;
		}

		// Return null if viewer size hasn't been calculated yet
		if (!this.#viewerSize) {
			return null;
		}

		return { ...this.#viewerSize } as ViewerSizeType;
	}

	// ============================================================================
	// PUBLIC METHODS (Lifecycle, configuration, queries)
	// ============================================================================

	/**
   * Update configuration
   */
	public updateConfig (newConfig: Partial<ViewportConfig>): void {
		// Validate maxFPS before applying
		if (newConfig.maxFPS !== undefined) {
			if (typeof newConfig.maxFPS === 'number' && newConfig.maxFPS > 0) {
				this.#config.maxFPS = newConfig.maxFPS;
			}
			// Remove maxFPS from newConfig to avoid overriding with invalid value
			const { maxFPS, ...validatedConfig } = newConfig;
			this.#config = { ...this.#config, ...validatedConfig };
		} else {
			this.#config = { ...this.#config, ...newConfig };
		}

		// Only update viewer size if we have valid container dimensions
		if (this.#containerSize && this.#containerSize.width > 0 && this.#containerSize.height > 0) {
			this._updateViewerSize();
		}

		// Publish config changes to controller
		this.publish('viewport-config', 'updated', this.#config);
		// console.log('viewport-config', this.#config);

		this._notifyListeners();
		this._notifyRender();
	}

	/**
	 * Get viewBox string for SVG
	 * Now incorporates zoom as a scaling factor applied to the stable viewer size
	 * Returns null when not ready (in initializing state)
	 */
	public getViewBox (): string | null {
		// Ensure viewer size is initialized
		if (!this.#viewerSize) {
			// Only try to update if we have valid container dimensions
			if (this.#containerSize && this.#containerSize.width > 0 && this.#containerSize.height > 0) {
				this._updateViewerSize();
				this._notifyRender();

				// If still no viewer size after update, return null (still initializing)
				if (!this.#viewerSize) {
					return null;
				}
			} else {
				// Return null if container is not ready (service is in initializing state)
				return null;
			}
		}

		// Apply zoom as a scaling factor to the stable viewer size
		const scaledWidth = this.#viewerSize.width / this.#state.zoom;
		const scaledHeight = this.#viewerSize.height / this.#state.zoom;
		const xCenter = (scaledWidth / 2) + (this.#state.xPan / 1);
		const yCenter = (scaledHeight / 2) + (this.#state.yPan / 1);

		// ViewBox format: "x y width height" where (x,y) is top-left corner
		// To center origin (0,0), we need: (-halfWidth, -halfHeight, width, height)
		return `${-xCenter},${-yCenter},${scaledWidth},${scaledHeight}`;
	}

	/**
	 * Set render callback for notifying controller when rendering is needed
	 */
	public setRenderCallback (callback: () => void): void {
		this.#renderCallback = callback;
	}

	/**
	* Subscribe to viewport state changes
	*/
	public subscribe (listener: (state: ViewportState) => void): () => void {
		this.#listeners.add(listener);
		return () => this.#listeners.delete(listener);
	}

	/**
	 * Subscribe to container size changes
	 */
	public subscribeToContainerSize (listener: (containerSize: { width: number; height: number; ratio: number }) => void): () => void {
		this.#containerSizeListeners.add(listener);
		return () => this.#containerSizeListeners.delete(listener);
	}

	/**
	 * Subscribe to viewer size changes
	 */
	public subscribeToViewerSize (listener: (viewerSize: ViewerSizeType | null) => void): () => void {
		this.#viewerSizeListeners.add(listener);
		return () => this.#viewerSizeListeners.delete(listener);
	}

	/**
	 * Manually trigger autozoom (fit to view) if enabled
	 */
	public triggerAutoZoom (): void {
		this._checkAutoZoom();
	}

	// ============================================================================
	// PRIVATE METHODS (Implementation details)
	// ============================================================================
	/**
	 * Register the SVG element with the viewport service
	 */
	private _registerSvg (svgElement: SVGElement): void {
		// Only register if it's a different element or no element was previously registered
		if (this.#svgElement === svgElement) {
			console.log('ViewportService: SVG element already registered, skipping duplicate registration');
			return;
		}

		this.#svgElement = svgElement;

		// Automatically detect and update container size from SVG element
		this._updateSizeFromSvg(svgElement);

		// Notify listeners that the SVG element has been registered
		this._notifyListeners();
	}

	/**
	 * Unregister the SVG element from the viewport service
	 */
	private _unregisterSvg (): void {
		this.#svgElement = null;
		// Notify listeners that the SVG element has been unregistered
		this._notifyListeners();
	}

	/**
	 * Automatically update container size from SVG element dimensions
	 */
	private _updateSizeFromSvg (svgElement: SVGElement): void {
		// Get the SVG element's dimensions
		const rect = svgElement.getBoundingClientRect();

		if (rect.width > 0 && rect.height > 0) {
			this._updateSize(rect.width, rect.height);
		} else {
			// Suppress warning in test environment
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: SVG element has zero dimensions, cannot auto-update container size');
			}

			// Try alternative methods to get dimensions
			const computedStyle = window.getComputedStyle(svgElement);
			const width = parseFloat(computedStyle.width) || svgElement.clientWidth;
			const height = parseFloat(computedStyle.height) || svgElement.clientHeight;

			if (width > 0 && height > 0) {
				console.log('ViewportService: Using alternative dimension detection:', { width, height });
				this._updateSize(width, height);
			}
		}
	}

	/**
	 * Resets view to default state
	 */
	private _resetView (): void {
		this.#state.zoom = viewportDefaults.zoom;
		this.#state.xPan = viewportDefaults.xPan;
		this.#state.yPan = viewportDefaults.yPan;
		this.#state.rotation = viewportDefaults.rotation;
		this.#state.targetZoom = viewportDefaults.zoom;
		this.#state.targetXPan = viewportDefaults.xPan;
		this.#state.targetYPan = viewportDefaults.yPan;
		this.#state.targetRotation = viewportDefaults.rotation;
		// Reset rotation history to default origin entry
		this.#state.rotationHistory = [
			{
				rotation: 0,
				panX: 0,
				panY: 0,
				timestamp: Date.now()
			}
		];
		// Only update viewer size if we have valid container dimensions
		if (this.#containerSize && this.#containerSize.width > 0 && this.#containerSize.height > 0) {
			this._updateViewerSize();
		}
		this._notifyListeners();
		this._notifyRender();
	}

	/**
	 * Resets container and viewer size state (useful when switching controllers)
	 */
	private _resetSizeState (): void {
		this.#containerSize = { width: 0, height: 0, ratio: 0 };
		this.#viewerSize = null;
		this.#svgElement = null;
		// Notify listeners about the reset
		this._notifyContainerSizeListeners();
		this._notifyViewerSizeListeners();
		this._notifyRender();
	}

	/**
	 * Sets zoom level with constraints
	 */
	private _setZoom (zoom: number): void {
		const constrainedZoom = Math.max(
			this.#config.minZoom,
			Math.min(this.#config.maxZoom, zoom)
		);

		this.#state.zoom = constrainedZoom;
		// Only update viewer size if we have valid container dimensions
		if (this.#containerSize && this.#containerSize.width > 0 && this.#containerSize.height > 0) {
			this._updateViewerSize();
		}
		this._notifyListeners();
		this._notifyRender();
	}

	/**
	 * Smoothly transitions to a new zoom level
	 */
	private _smoothZoom (targetZoom: number, duration?: number): void {
		if (!this.#config.smoothZoomEnabled) {
			// If resetting zoom to default, also reset rotation history baseline
			if (targetZoom === viewportDefaults.zoom) {
				this.#state.rotationHistory = [
{
					rotation: 0,
					panX: 0,
					panY: 0,
					timestamp: Date.now()
				}
];
			}
			this._setZoom(targetZoom);
			return;
		}

		// Cancel any existing zoom animation
		this._cancelZoomAnimation();

		const startZoom = this.#state.zoom;
		const zoomDelta = targetZoom - startZoom;

		// If targeting default zoom during a smooth reset, also reset rotation history baseline
		if (targetZoom === viewportDefaults.zoom) {
			this.#state.rotationHistory = [
				{
					rotation: 0,
					panX: 0,
					panY: 0,
					timestamp: Date.now()
				}
			];
			this._notifyListeners();
		}

		if (Math.abs(zoomDelta) < this.#config.zoomSnap) return;

		this.#isZooming = true;
		const startTime = performance.now();
		const animationDuration = duration || this.#config.smoothZoomDuration;

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / animationDuration, 1);

			// Apply configured easing function
			let easeOut: number = 1 - ((1 - progress) ** 3);
			switch (this.#config.smoothZoomEasing) {
				case 'linear':
					easeOut = progress;
					break;
				case 'ease-in':
					easeOut = progress ** 3;
					break;
				case 'ease-in-out':
					easeOut = progress < 0.5 ? 2 * progress ** 2 : 1 - 2 * (1 - progress) ** 2;
					break;
				case 'ease-out':
				default:
					easeOut = 1 - ((1 - progress) ** 3);
					break;
			}

			const newZoom = startZoom + (zoomDelta * easeOut);
			// If resetting zoom to default, also reset rotation history baseline
			if (progress === 0 && targetZoom === viewportDefaults.zoom) {
				this.#state.rotationHistory = [
{
					rotation: 0,
					panX: 0,
					panY: 0,
					timestamp: Date.now()
				}
];
			}
			this._setZoom(newZoom);

			if (progress < 1) {
				this.#zoomAnimationId = this._requestThrottledAnimationFrame(animate);
			} else {
				this.#isZooming = false;
				this.#zoomAnimationId = null;
				// Process next item in queue
				this._processZoomQueue();
			}
		};

		this.#zoomAnimationId = this._requestThrottledAnimationFrame(animate);
	}

	/**
	 * Cancels any ongoing zoom animation
	 */
	private _cancelZoomAnimation (): void {
		if (this.#zoomAnimationId) {
			cancelAnimationFrame(this.#zoomAnimationId);
			this.#zoomAnimationId = null;
		}
		this.#isZooming = false;
	}

	/**
	 * Sets pan position
	 */
	private _setPan (x: number, y: number): void {
		this.#state.xPan = x;
		this.#state.yPan = y;
		this._updateViewerSize();
		this._notifyListeners();
		this._notifyRender();
	}

	/**
	 * Smoothly transitions to a new pan position
	 */
	private _smoothPan (targetX: number, targetY: number, duration?: number): void {
		if (!this.#config.smoothPanEnabled) {
			this._setPan(targetX, targetY);
			return;
		}

		// Cancel any existing pan animation
		this._cancelPanAnimation();

		const startX = this.#state.xPan;
		const startY = this.#state.yPan;
		const panDeltaX = targetX - startX;
		const panDeltaY = targetY - startY;

		// If the change in pan position is smaller than the configured pan sensitivity in
		// both x and y directions, do not start a smooth pan animation.
		if (Math.abs(panDeltaX) < this.#config.panSensitivity && Math.abs(panDeltaY) < this.#config.panSensitivity) return;

		this.#isPanning = true;
		const startTime = performance.now();
		const animationDuration = duration || this.#config.smoothPanDuration;

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / animationDuration, 1);

			// Apply configured easing function
			let easeOut: number = 1 - ((1 - progress) ** 3);
			switch (this.#config.smoothPanEasing) {
				case 'linear':
					easeOut = progress;
					break;
				case 'ease-in':
					easeOut = progress ** 3;
					break;
				case 'ease-in-out':
					easeOut = progress < 0.5 ? 2 * progress ** 2 : 1 - 2 * (1 - progress) ** 2;
					break;
				case 'ease-out':
				default:
					easeOut = 1 - ((1 - progress) ** 3);
					break;
			}

			const newX = startX + (panDeltaX * easeOut);
			const newY = startY + (panDeltaY * easeOut);
			this._setPan(newX, newY);

			if (progress < 1) {
				this.#panAnimationId = this._requestThrottledAnimationFrame(animate);
			} else {
				this.#isPanning = false;
				this.#panAnimationId = null;
				// Process next item in queue
				this._processPanQueue();
			}
		};

		this.#panAnimationId = this._requestThrottledAnimationFrame(animate);
	}

	/**
	 * Cancels any ongoing pan animation
	 */
	private _cancelPanAnimation (): void {
		if (this.#panAnimationId) {
			cancelAnimationFrame(this.#panAnimationId);
			this.#panAnimationId = null;
		}
		this.#isPanning = false;
	}

	/**
	 * Sets the rotation of the viewport
	 */
	private _setRotation (rotation: number): void {
		// If this is a direct rotation call (not from rotation history),
		// we need to update the rotation history to maintain consistency
		// Update rotation and append a tracking entry if pan changed since last entry
		const last = this.#state.rotationHistory[this.#state.rotationHistory.length - 1];
		this.#state.rotation = rotation;
		if (!last || Math.abs(last.panX - this.#state.xPan) > 0.1 || Math.abs(last.panY - this.#state.yPan) > 0.1) {
			this.#state.rotationHistory.push({
				rotation: this.#state.rotation,
				panX: this.#state.xPan,
				panY: this.#state.yPan,
				timestamp: Date.now()
			});
		}

		this._updateViewerSize();
		this._notifyListeners();
		this._notifyRender();
	}

	/**
	 * Smoothly transitions to a new rotation angle
	 */
	private _smoothRotation (targetRotation: number, duration?: number): void {
		console.log('Smooth rotation called:', {
			targetRotation,
			duration,
			smoothRotationEnabled: this.#config.smoothRotationEnabled,
			smoothRotationDuration: this.#config.smoothRotationDuration,
			smoothRotationEasing: this.#config.smoothRotationEasing
		});

		if (!this.#config.smoothRotationEnabled) {
			console.log('Smooth rotation disabled, using direct rotation');
			this._setRotation(targetRotation);
			return;
		}

		// Cancel any existing rotation animation
		this._cancelRotationAnimation();

		const startRotation = this.#state.rotation;
		let rotationDelta = targetRotation - startRotation;

		// Handle rotation wrapping (e.g., 350° to 10° should rotate 20°, not -340°)
		if (rotationDelta > 180) rotationDelta -= 360;
		if (rotationDelta < -180) rotationDelta += 360;

		if (Math.abs(rotationDelta) < 1) return; // Small threshold for rotation

		this.#isRotating = true;
		const startTime = performance.now();
		const animationDuration = duration || this.#config.smoothRotationDuration;

		console.log('Starting smooth rotation animation:', {
			startRotation,
			targetRotation,
			rotationDelta,
			animationDuration
		});

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / animationDuration, 1);

			if (progress === 0) {
				console.log('Rotation animation frame started');
			}

			// Apply configured easing function
			let easeOut: number = 1 - ((1 - progress) ** 3);
			switch (this.#config.smoothRotationEasing) {
				case 'linear':
					easeOut = progress;
					break;
				case 'ease-in':
					easeOut = progress ** 3;
					break;
				case 'ease-in-out':
					easeOut = progress < 0.5 ? 2 * progress ** 2 : 1 - 2 * (1 - progress) ** 2;
					break;
				case 'ease-out':
				default:
					easeOut = 1 - ((1 - progress) ** 3);
					break;
			}

			const newRotation = startRotation + (rotationDelta * easeOut);
			// Use setter to ensure listeners are notified and state stays consistent
			this._setRotation(newRotation);

			if (progress < 1) {
				this.#rotationAnimationId = this._requestThrottledAnimationFrame(animate);
			} else {
				console.log('Rotation animation completed');
				this.#isRotating = false;
				this.#rotationAnimationId = null;
				// Process next item in queue
				this._processRotationQueue();
			}
		};

		this.#rotationAnimationId = this._requestThrottledAnimationFrame(animate);
	}

	/**
	 * Cancels any ongoing rotation animation
	 */
	private _cancelRotationAnimation (): void {
		if (this.#rotationAnimationId) {
			cancelAnimationFrame(this.#rotationAnimationId);
			this.#rotationAnimationId = null;
		}
		this.#isRotating = false;
	}

	// ============================================================================
	// ANIMATION QUEUE MANAGEMENT
	// ============================================================================

	/**
	 * Adds a zoom animation request to the queue
	 */
	private _queueZoomAnimation (target: number, duration?: number): void {
		this.#zoomQueue.push({ target, duration });
		this.#state.targetZoom = target;
		this._notifyListeners();
		this._processZoomQueue();
	}

	/**
	 * Processes the zoom animation queue
	 */
	private _processZoomQueue (): void {
		if (this.#isZooming || this.#zoomQueue.length === 0) {
			return;
		}

		const next = this.#zoomQueue.shift();
		if (next) {
			this._smoothZoom(next.target, next.duration);
		}
	}

	/**
	 * Adds a pan animation request to the queue
	 */
	private _queuePanAnimation (targetX: number, targetY: number, duration?: number): void {
		this.#panQueue.push({ targetX, targetY, duration });
		this.#state.targetXPan = targetX;
		this.#state.targetYPan = targetY;
		this._notifyListeners();
		this._processPanQueue();
	}

	/**
	 * Processes the pan animation queue
	 */
	private _processPanQueue (): void {
		if (this.#isPanning || this.#panQueue.length === 0) {
			return;
		}

		const next = this.#panQueue.shift();
		if (next) {
			this._smoothPan(next.targetX, next.targetY, next.duration);
		}
	}

	/**
	 * Adds a rotation animation request to the queue
	 */
	private _queueRotationAnimation (target: number, duration?: number): void {
		this.#rotationQueue.push({ target, duration });
		this.#state.targetRotation = target;
		this._notifyListeners();
		this._processRotationQueue();
	}

	/**
	 * Processes the rotation animation queue
	 */
	private _processRotationQueue (): void {
		if (this.#isRotating || this.#rotationQueue.length === 0) {
			return;
		}

		const next = this.#rotationQueue.shift();
		if (next) {
			this._smoothRotation(next.target, next.duration);
		}
	}


	/**
	 * Fits content to view
	 * If no contentBounds is provided, attempts to fit to the SVG content
	 * Now simplified to work with stable viewer size and zoom as scaling factor
	 */
	private _fitToView (contentBounds?: { x?: number; y?: number; width: number; height: number }): void {
		// Only proceed if we have valid container dimensions
		if (!this.#containerSize || this.#containerSize.width <= 0 || this.#containerSize.height <= 0) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Cannot fit to view - invalid container dimensions');
			}
			return;
		}

		// If no contentBounds provided, try to get bounds from SVG content
		let bounds = contentBounds;
		if (!bounds) {
			bounds = this._getSvgContentBounds();
		if (!bounds) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Cannot fit to view - no content bounds available');
			}
			return;
		}
		}
		// console.log('bounds', bounds);

		const { x = 0, y = 0, width, height } = bounds;

		// Calculate optimal zoom to fit content to the viewport
		// We want the content to fill the viewport with some padding
		const padding = this.#config.fitToViewPadding;

		// Use stable viewer size for zoom calculations (no zoom dependency)
		if (!this.#viewerSize) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Cannot fit to view - viewer size not initialized');
			}
			return;
		}

		const viewportWidth = this.#viewerSize.width;
		const viewportHeight = this.#viewerSize.height;

		const scaleX = (viewportWidth * (1 - padding)) / width;
		const scaleY = (viewportHeight * (1 - padding)) / height;

		// Use the smaller scale to ensure content fits in both dimensions
		// This will zoom in when content is small, zoom out when content is large
		const optimalZoom = Math.min(scaleX, scaleY);

		// Calculate the pan offset needed to center the content in the viewport
		// The pan should center the content by offsetting from the viewport center
		const targetPanX = -x;
		const targetPanY = -y;

		// console.log('Fit to view calculation:', {
		// 	viewerSize: { width: viewportWidth, height: viewportHeight },
		// 	contentBounds: { width, height, centerX: x, centerY: y },
		// 	scaleX,
		// 	scaleY,
		// 	optimalZoom,
		// 	targetPan: { x: targetPanX, y: targetPanY },
		// 	currentZoom: this.#state.zoom
		// });

		// Check if current pan is already optimal (within a small tolerance)
		const panTolerance = 0.1; // 0.1 unit tolerance
		const isPanOptimal = Math.abs(this.#state.xPan - targetPanX) < panTolerance &&
			Math.abs(this.#state.yPan - targetPanY) < panTolerance;

		// Reset rotation history baseline when fitting view
		this.#state.rotationHistory = [
{
			rotation: 0,
			panX: 0,
			panY: 0,
			timestamp: Date.now()
		}
];
		this._smoothZoom(optimalZoom);

		// Only apply pan changes if they're significantly different from current state
		if (isPanOptimal) {
			// pan is already optimal, skip
		} else {
			this._smoothPan(targetPanX, targetPanY);
		}
	}

	/**
	 * Gets the content bounds from the registered SVG by looking for the main group element
	 * Returns null if no content bounds can be determined
	 */
	private _getSvgContentBounds (): { x: number; y: number; width: number; height: number } | null {
		try {
			// Use the registered SVG element
		if (!this.#svgElement) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: No SVG element registered');
			}
			return null;
		}

			// Look for the main group element (usually the first <g> element)
			const groupElement = this.#svgElement.querySelector('g#viewport-content-group') as SVGGElement;
		if (!groupElement) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: No group element found in registered SVG');
			}
			return null;
		}

			// A rotated viewport needs the leaves measured; unrotated keeps the cheap single getBBox
			if (this.#state.rotation % 360 !== 0) {
				const rotated = getRenderedContentBounds(groupElement);
				if (rotated) {
					return { x: rotated.x + (rotated.width / 2), y: rotated.y + (rotated.height / 2), width: rotated.width, height: rotated.height };
				}
			}

			// Get the bounding box of the group element
			const bbox = groupElement.getBBox();
		if (bbox.width === 0 || bbox.height === 0) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Group element has zero dimensions');
			}
			return null;
		}
			// console.log('bbox', bbox);
		return {
			x: bbox.x + (bbox.width / 2), // Center X coordinate
			y: bbox.y + (bbox.height / 2), // Center Y coordinate
			width: bbox.width,
			height: bbox.height
		};
		} catch (error) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Error getting SVG content bounds:', error);
			}
			return null;
		}
	}

	/**
	 * Transforms world coordinates to viewport coordinates
	 */
	private _transformCoordinates (worldX: number, worldY: number): { x: number; y: number } {
		const radians = (this.#state.rotation * Math.PI) / 180;
		const cos = Math.cos(radians);
		const sin = Math.sin(radians);

		// Apply rotation transformation
		const rotatedX = (worldX * cos) - (worldY * sin);
		const rotatedY = (worldX * sin) + (worldY * cos);

		// Apply viewport transformations
		return {
			x: (rotatedX + this.#state.xPan) * this.#state.zoom,
			y: (rotatedY + this.#state.yPan) * this.#state.zoom
		};
	}

	/**
	 * Gets SVG transform string for groups
	 */
	private _getSvgTransform (): string {
		const transforms = [
			`translate(${this.#state.xPan}, ${this.#state.yPan})`,
			`scale(${this.#config.xScale * this.#state.zoom}, ${this.#config.yScaleFactor * this.#state.zoom})`
		];

		// Add rotation if not 0
		if (this.#state.rotation !== 0) {
			transforms.push(`rotate(${this.#state.rotation})`);
		}

		return transforms.join(' ');
	}

	/**
	 * Updates container size and recalculates viewport
	 */
	private _updateSize (width: number, height: number): void {
		// Validate dimensions before updating
		if (width <= 0 || height <= 0) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Invalid dimensions provided to _updateSize:', { width, height });
			}
			return;
		}

		// Check if size actually changed to prevent infinite loops
		if (this.#containerSize.width === width && this.#containerSize.height === height) {
			return; // No change, no need to update
		}

		// console.log('ViewportService: Updating container size:', {
		// 	old: this.#containerSize,
		// 	new: { width, height, ratio: width / height }
		// });

		this.#containerSize = {
			width,
			height,
			ratio: width / height
		};

		// Automatically calculate viewer size when container dimensions are available
		// This ensures the service moves from initializing to ready state
		this._updateViewerSize();

		// Trigger autozoom if enabled
		this._checkAutoZoom();

		this._notifyListeners();
		this._notifyContainerSizeListeners();
		this._notifyRender();
	}

	/**
	 * Updates viewer size based on current container dimensions
	 */
	private _updateViewerSize (): void {
		// Only update viewer size if we have valid container dimensions
		if (!this.#containerSize || this.#containerSize.width <= 0 || this.#containerSize.height <= 0) {
			// Suppress warning in test environment
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Cannot update viewer size - invalid container dimensions');
			}
			return;
		}

		try {
			this.#viewerSize = getViewerSize({
				containerSize: this.#containerSize,
				panning: { x: this.#state.xPan, y: this.#state.yPan }
			});
		} catch (error) {
			if (process.env.NODE_ENV !== 'test') {
				console.warn('ViewportService: Failed to calculate viewer size:', error);
			}
			// Don't set fallback values - keep service in initializing state
			this.#viewerSize = null;
		}

		// Notify listeners about viewer size changes
		this._notifyViewerSizeListeners();
	}

	/**
	 * Notify all listeners of state changes
	 */
	private _notifyListeners (): void {
		this.#listeners.forEach((listener) => listener(this.state));
		// Publish viewport state changes to controller
		this.publish('viewport-state', 'updated', this.state);
	}

	/**
	 * Notify all container size listeners
	 */
	private _notifyContainerSizeListeners (): void {
		this.#containerSizeListeners.forEach((listener) => listener(this.#containerSize));
		// Publish container size changes to controller
		this.publish('viewport-container-size', 'updated', this.#containerSize);
	}

	/**
	 * Notify all viewer size listeners
	 */
	private _notifyViewerSizeListeners (): void {
		this.#viewerSizeListeners.forEach((listener) => listener(this.#viewerSize));
		// Publish viewer size changes to controller
		this.publish('viewport-viewer-size', 'updated', this.#viewerSize);
	}

	/**
	 * Create a throttled animation frame callback based on maxFPS setting
	 * @param callback The animation callback function
	 * @returns A throttled animation frame ID
	 */
	private _requestThrottledAnimationFrame (callback: (time: number) => void): number {
		const { maxFPS } = this.#config;

		// If maxFPS is 0 or invalid, use regular requestAnimationFrame
		if (!maxFPS || maxFPS <= 0) {
			return requestAnimationFrame(callback);
		}

		const targetInterval = 1000 / maxFPS;
		let lastTime = 0;

		const throttledCallback = (currentTime: number) => {
			if (currentTime - lastTime >= targetInterval) {
				callback(currentTime);
				lastTime = currentTime;
			} else {
				requestAnimationFrame(throttledCallback);
			}
		};

		return requestAnimationFrame(throttledCallback);
	}

	/**
	 * Check if autozoom should be triggered and call fitToView if needed
	 */
	private _checkAutoZoom (): void {
		// Only trigger autozoom if enabled and we have valid dimensions
		if (!this.#config.autoZoom || !this.#containerSize || this.#containerSize.width <= 0 || this.#containerSize.height <= 0) {
			return;
		}

		// Only trigger autozoom if we have a registered SVG element with content
		if (!this.#svgElement) {
			return;
		}

		// Use a small delay to ensure the DOM has been updated
		// This prevents infinite loops and ensures content is ready
		setTimeout(() => {
			// Double-check that autozoom is still enabled (in case it was disabled during the timeout)
			if (this.#config.autoZoom) {
				this._fitToView();
			}
		}, 0);
	}

	/**
	 * Notify controller that rendering is needed
	 */
	private _notifyRender (): void {
		if (this.#renderCallback) {
			this.#renderCallback();
		}
	}

	// ============================================================================
	// STATIC VARIABLES
	// ============================================================================
	// No static variables in this class

	// ============================================================================
	// STATIC METHODS
	// ============================================================================
	// No static methods in this class
}
